import { useState, useEffect } from 'react';
import config from './config';

const InstagramOCR = ({ onDataExtracted }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [cardCount, setCardCount] = useState(null);
  const [timeUntilReset, setTimeUntilReset] = useState(null);

  useEffect(() => {
    // Fetch initial card count
    fetchCardCount();
    // Set up interval to update count every minute
    const interval = setInterval(fetchCardCount, 60000);
    return () => clearInterval(interval);
  }, []);

  const fetchCardCount = async () => {
    try {
      const response = await fetch(`${config.apiUrl}/card-count`);
      const data = await response.json();
      setCardCount(data.count);
      setTimeUntilReset(data.timeUntilReset);
    } catch (error) {
      console.error('Error fetching card count:', error);
    }
  };

  const formatTimeRemaining = (ms) => {
    if (!ms) return '';
    const minutes = Math.ceil(ms / 60000);
    return `${minutes} minute${minutes !== 1 ? 's' : ''}`;
  };

  const compressImage = (file, maxWidth = 1200, quality = 0.8) => {
    return new Promise((resolve) => {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      const img = new Image();
      
      img.onload = () => {
        // Calculate new dimensions
        const ratio = Math.min(maxWidth / img.width, maxWidth / img.height);
        canvas.width = img.width * ratio;
        canvas.height = img.height * ratio;
        
        // Draw and compress
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
        canvas.toBlob(resolve, 'image/jpeg', quality);
      };
      
      img.src = URL.createObjectURL(file);
    });
  };

  const encodeImage = async (file) => {
    // Check if we're on mobile (likely iPhone) and file is large
    const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
    const isLargeFile = file.size > 2 * 1024 * 1024; // 2MB
    
    let processedFile = file;
    
    // Compress image on mobile or for large files
    if (isMobile && isLargeFile) {
      console.log('Compressing image for mobile device...');
      processedFile = await compressImage(file);
    }
    
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        const base64 = reader.result.split(',')[1];
        resolve(base64);
      };
      reader.onerror = reject;
      reader.readAsDataURL(processedFile);
    });
  };

  const extractTextWithGroq = async (base64Image) => {
    try {
      // Add timeout for mobile networks
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 30000); // 30 second timeout
      
      const response = await fetch(`${config.apiUrl}/analyze`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ image: base64Image }),
        signal: controller.signal
      });

      clearTimeout(timeoutId);

      // Handle 204 No Content specifically
      if (response.status === 204) {
        throw new Error('Server returned empty response. This may be due to image size or network issues on mobile devices. Please try with a smaller image.');
      }

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        if (response.status === 429) {
          throw new Error(`Server limit reached. Please try again in ${formatTimeRemaining(errorData.timeUntilReset)}.`);
        }
        
        // Mobile-specific error messages
        const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
        if (isMobile && (response.status === 413 || response.status === 500)) {
          throw new Error('Image too large for mobile processing. Please try with a smaller screenshot.');
        }
        
        throw new Error(errorData.message || 'Failed to analyze image');
      }

      const data = await response.json();
      setCardCount(data.cardCount);
      
      // Return entire response (could be cached data or just text)
      return data;
    } catch (error) {
      // Enhanced error logging for debugging mobile issues
      console.error('API Error Details:', {
        message: error.message,
        userAgent: navigator.userAgent,
        imageSize: base64Image ? base64Image.length : 0,
        timestamp: new Date().toISOString()
      });
      
      throw new Error(`API error: ${error.message}`);
    }
  };

  const parseInstagramData = (text) => {
    const result = {
      id: "",
      name: "",
      bio: "",
      posts: 0,
      followers: 0,
      following: 0,
      highlights: 0,
      contentType: "",
      profileBrief: "",
      rawOcrText: text,
      rawFollowersText: "",
      rawFollowingText: ""
    };
    
    function extractRawCount(text, keyword) {
      const keywordPos = text.toLowerCase().indexOf(keyword.toLowerCase());
      if (keywordPos === -1) return "";
      
      const start = Math.max(0, keywordPos - 20);
      const end = Math.min(text.length, keywordPos + keyword.length + 5);
      const chunk = text.substring(start, end);
      
      const pattern = new RegExp(`(\\d+\\.?\\d*\\s*[KkMm]?\\s*${keyword})`, 'i');
      const match = chunk.match(pattern);
      
      return match ? match[1].trim() : "";
    }
    
    function extractNumberValue(text, keyword) {
      const keywordPos = text.toLowerCase().indexOf(keyword.toLowerCase());
      if (keywordPos === -1) return 0;
      
      const start = Math.max(0, keywordPos - 20);
      const end = Math.min(text.length, keywordPos + keyword.length + 5);
      const chunk = text.substring(start, end);
      
      const matches = chunk.match(/([\d.,]+)\s*([km])?/i);
      if (!matches) return 0;
      
      const numStr = matches[1].replace(/,/g, '');
      const suffix = matches[2] ? matches[2].toLowerCase() : '';
      
      let value = parseFloat(numStr);
      if (suffix === 'k') value *= 1000;
      if (suffix === 'm') value *= 1000000;
      
      return Math.round(value) || 0;
    }
    
    result.rawFollowersText = extractRawCount(text, "followers");
    result.rawFollowingText = extractRawCount(text, "following");
    
    const lines = text.split('\n');
    const cleanedLines = lines
      .filter(line => line.trim())
      .map(line => line.replace(/^\s*(?:\*|-|[0-9]+\.)\s*/, '').trim());
    
    for (let i = 0; i < cleanedLines.length; i++) {
      const line = cleanedLines[i];
      if (line.startsWith('@')) {
        result.id = line;
        if (i + 1 < cleanedLines.length) {
          result.name = cleanedLines[i + 1];
          if (i + 2 < cleanedLines.length) {
            const bioStart = i + 2;
            const bioLines = [];
            for (let j = bioStart; j < cleanedLines.length; j++) {
              if (cleanedLines[j].toLowerCase().includes('posts') || 
                  cleanedLines[j].toLowerCase().includes('followers') || 
                  cleanedLines[j].toLowerCase().includes('following')) {
                break;
              }
              bioLines.push(cleanedLines[j]);
            }
            result.bio = bioLines.join(' ').trim();
          }
        }
        break;
      }
    }
    
    for (const line of cleanedLines) {
      const lineLower = line.toLowerCase();
      
      if (lineLower.includes('posts')) {
        result.posts = extractNumberValue(lineLower, 'posts');
      }
      
      if (lineLower.includes('followers')) {
        result.followers = extractNumberValue(lineLower, 'followers');
      }
      
      if (lineLower.includes('following')) {
        result.following = extractNumberValue(lineLower, 'following');
      }
      
      if (lineLower.includes('highlights')) {
        result.highlights = extractNumberValue(lineLower, 'highlights');
      }
    }
    
    for (let i = 0; i < cleanedLines.length; i++) {
      const line = cleanedLines[i].toLowerCase();
      
      if (line.includes('content_type:') || line.startsWith('content type:') || 
          (line.includes('content') && line.includes('type'))) {
        result.contentType = cleanedLines[i].replace(/content_type:|content type:/i, '').trim();
      }
      
      if (line.includes('profile_brief:') || line.startsWith('profile brief:') || 
          (line.includes('profile') && line.includes('brief'))) {
        let briefLines = [];
        briefLines.push(cleanedLines[i].replace(/profile_brief:|profile brief:/i, '').trim());
        
        for (let j = i + 1; j < cleanedLines.length; j++) {
          const nextLine = cleanedLines[j].toLowerCase();
          if (nextLine.includes('content_type:') || nextLine.includes('highlights:') ||
              nextLine.includes('@') || nextLine.includes('posts') || 
              nextLine.includes('followers') || nextLine.includes('following')) {
            break;
          }
          briefLines.push(cleanedLines[j]);
        }
        
        result.profileBrief = briefLines.join(' ').trim();
      }
    }
    
    return result;
  };

  const handleImageUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    setLoading(true);
    setError(null);

    try {
      const base64Image = await encodeImage(file);
      const backendResponse = await extractTextWithGroq(base64Image);
      
      // Check if we got cached data from backend
      if (backendResponse.existingCard) {
        console.log("🎯 CACHED DATA DETECTED IN INSTAGRAM OCR!");
        // Pass the entire cached response to parent
        onDataExtracted(backendResponse);
      } else {
        // For new cards, parse the OCR text
        const data = parseInstagramData(backendResponse.text || backendResponse);
      onDataExtracted(data);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="instagram-ocr">
      <input
        type="file"
        accept="image/*"
        onChange={handleImageUpload}
        disabled={loading || (cardCount !== null && cardCount >= 100000)}
        style={{ display: 'none' }}
        id="instagram-image-upload"
      />
      <label 
        htmlFor="instagram-image-upload"
        className="upload-button"
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          cursor: (loading || (cardCount !== null && cardCount >= 100000)) ? 'not-allowed' : 'pointer',
          opacity: (loading || (cardCount !== null && cardCount >= 100000)) ? 0.7 : 1
        }}
      >
        <div className="upload-icon">
          {loading ? (
            <div className="loading-spinner" />
          ) : (
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
              <polyline points="17 8 12 3 7 8" />
              <line x1="12" y1="3" x2="12" y2="15" />
            </svg>
          )}
        </div>
        <span className="upload-text">
          {loading ? 'Processing...' : 
           cardCount !== null && cardCount >= 100000 ? 
           `Server limit reached. Try again in ${formatTimeRemaining(timeUntilReset)}` :
           'Upload Instagram Profile'}
        </span>
      </label>
      {cardCount !== null && (
        <div className="card-count" style={{ 
          marginTop: '10px', 
          fontSize: '0.9em', 
          color: '#666' 
        }}>
          Cards created: {cardCount}/100,000
        </div>
      )}
      {error && (
        <div className="error-message" style={{ color: 'red', marginTop: '10px' }}>
          {error}
        </div>
      )}
    </div>
  );
};

export default InstagramOCR; 